import { useState } from "react";
import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Section } from "../components/Section";
import { CTA } from "../components/CTA";
import { DOMAINS, INDUSTRIES } from "../lib/content";
import { useTranslation } from "../lib/i18n";
import { sendEmail } from "../lib/email-helper";
import { supabase } from "../lib/supabase";

interface FormData {
  name: string;
  organization: string;
  email: string;
  industry: string;
  domain: string;
  message: string;
}

const INITIAL: FormData = {
  name: "",
  organization: "",
  email: "",
  industry: "",
  domain: "",
  message: "",
};

export default function ContactPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormData>(INITIAL);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  const update = (key: keyof FormData, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setError(t("contact.form.error"));
      return;
    }
    setStatus("submitting");
    setError(null);
    try {
      const emailBody = `<h2>New Contact Form Submission</h2>
<p><strong>Name:</strong> ${form.name}</p>
<p><strong>Organization:</strong> ${form.organization || "N/A"}</p>
<p><strong>Email:</strong> ${form.email}</p>
<p><strong>Industry:</strong> ${form.industry || "N/A"}</p>
<p><strong>Domain:</strong> ${form.domain || "N/A"}</p>
<p><strong>Message:</strong></p>
<p>${form.message.replace(/\n/g, "<br>")}</p>`;

      await sendEmail({
        to: "brian.smith@sentinel-architecture.com",
        cc: "brian.smith@e-industries.net",
        subject: `New Contact Form Submission — ${form.name}`,
        body: emailBody,
        email_type: "contact_form",
      });

      await supabase.from("sentinel_contact_submissions").insert({
        name: form.name,
        organization: form.organization || null,
        email: form.email,
        industry: form.industry || null,
        domain: form.domain || null,
        message: form.message,
      });

      setStatus("success");
      setForm(INITIAL);
    } catch {
      setError("Failed to send message. Please try again or email us directly.");
      setStatus("error");
    }
  };

  return (
    <>
      <SEO
        title="Contact — Sentinel"
        description="Contact the Sentinel team. Tell us your organization, industry, and domain of interest."
        canonical="/contact"
        image="https://images.pexels.com/photos/36714212/pexels-photo-36714212.jpeg?auto=compress&cs=tinysrgb&w=1200"
      />
      <PageHero
        eyebrow={t("contact.page.eyebrow")}
        title={t("contact.page.title")}
        description={t("contact.page.description")}
        bgVariant="slate"
        bgImage="https://images.pexels.com/photos/36714212/pexels-photo-36714212.jpeg?auto=compress&cs=tinysrgb&w=1600"
      />

      <Section>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-6)" }}>
          <div>
            <h3 style={{ marginBottom: "1rem" }}>{t("contact.form.title")}</h3>
            {status === "success" ? (
              <div
                className="card"
                style={{
                  borderColor: "var(--accent-400)",
                  background: "var(--accent-50)",
                  textAlign: "center",
                  padding: "var(--space-6)",
                }}
              >
                <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>&#10003;</div>
                <h3 style={{ marginBottom: "0.5rem" }}>{t("contact.form.success.title")}</h3>
                <p className="text-muted">
                  {t("contact.form.success.body")}
                </p>
                <button
                  className="btn btn-secondary"
                  style={{ marginTop: "1rem" }}
                  onClick={() => setStatus("idle")}
                >
                  {t("contact.form.sendAnother")}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label" htmlFor="name">{t("contact.form.name")}</label>
                  <input
                    id="name"
                    className="form-input"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder={t("contact.form.namePlaceholder")}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="org">{t("contact.form.organization")}</label>
                  <input
                    id="org"
                    className="form-input"
                    value={form.organization}
                    onChange={(e) => update("organization", e.target.value)}
                    placeholder={t("contact.form.organizationPlaceholder")}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="email">{t("contact.form.email")}</label>
                  <input
                    id="email"
                    type="email"
                    className="form-input"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder={t("contact.form.emailPlaceholder")}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="industry">{t("contact.form.industry")}</label>
                  <select
                    id="industry"
                    className="form-select"
                    value={form.industry}
                    onChange={(e) => update("industry", e.target.value)}
                  >
                    <option value="">{t("contact.form.selectIndustry")}</option>
                    {INDUSTRIES.map((ind) => (
                      <option key={ind.id} value={ind.name}>{ind.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="domain">{t("contact.form.domain")}</label>
                  <select
                    id="domain"
                    className="form-select"
                    value={form.domain}
                    onChange={(e) => update("domain", e.target.value)}
                  >
                    <option value="">{t("contact.form.selectDomain")}</option>
                    {DOMAINS.map((d) => (
                      <option key={d.id} value={d.name}>{d.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="message">{t("contact.form.message")}</label>
                  <textarea
                    id="message"
                    className="form-textarea"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder={t("contact.form.messagePlaceholder")}
                  />
                </div>
                {error && <p className="form-error">{error}</p>}
                <button
                  type="submit"
                  className="btn btn-primary btn-lg btn-block"
                  disabled={status === "submitting"}
                >
                  {status === "submitting" ? t("contact.form.sending") : t("contact.form.submit")}
                </button>
              </form>
            )}
          </div>

          <div>
            <h3 style={{ marginBottom: "1rem" }}>{t("contact.why.title")}</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
              {[
                { t: t("contact.why1.title"), d: t("contact.why1.desc") },
                { t: t("contact.why2.title"), d: t("contact.why2.desc") },
                { t: t("contact.why3.title"), d: t("contact.why3.desc") },
                { t: t("contact.why4.title"), d: t("contact.why4.desc") },
              ].map((item) => (
                <div key={item.t} className="card">
                  <h4 style={{ fontSize: "1rem", marginBottom: "0.375rem" }}>{item.t}</h4>
                  <p className="text-muted" style={{ fontSize: "0.9375rem" }}>{item.d}</p>
                </div>
              ))}
            </div>
            <div style={{ marginTop: "var(--space-4)" }}>
              <CTA to="/register" variant="primary">{t("cta.registerInstead")}</CTA>
            </div>
          </div>
        </div>
        <style>{`
          @media (max-width: 768px) {
            .section .container > div:first-child > div:first-child {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>
      </Section>
    </>
  );
}
